import React, { useEffect, useState } from "react";
import { useAccount } from "wagmi";
import AOS from "aos";
import Menu from "../components/Menu";
import Footer from "../components/Footer";
import Preloader from "../components/sections/Preloader";
import ScrollTop from "../components/sections/ScrollTop";
import ConnectWallet from "../components/ConnectWallet";
import "../styles/mission.css";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const Mission = () => {
  const { isConnected, address } = useAccount();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completingMission, setCompletingMission] = useState(null);
  const [stats, setStats] = useState({ total: 0, completed: 0, pending: 0 });

  useEffect(() => {
    AOS.init({
      duration: 2000,
      once: true,
    });
    fetchMissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, address]);

  const fetchMissions = async () => {
    try {
      setLoading(true);
      const url =
        isConnected && address
          ? `${API_BASE_URL}/api/mission?userId=${address}`
          : `${API_BASE_URL}/api/mission`;

      const response = await fetch(url);
      const data = await response.json();

      if (response.ok && data.success) {
        setMissions(data.data);

        // Calculate stats
        const completed = data.data.filter((m) => m.isCompleted).length;
        setStats({
          total: data.data.length,
          completed: completed,
          pending: data.data.length - completed,
        });
      }
    } catch (error) {
      console.error("Error fetching missions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMissionAction = async (mission) => {
    if (!isConnected || !address) {
      alert("Please connect your wallet first to complete missions!");
      return;
    }

    // Build Twitter URL based on action
    let url = "";
    switch (mission.action) {
      case "like":
        url = mission.tweetId
          ? `https://twitter.com/intent/like?tweet_id=${mission.tweetId}`
          : mission.twitterUrl || "https://twitter.com/CVMechNFT";
        break;
      case "retweet":
        url = mission.tweetId
          ? `https://twitter.com/intent/retweet?tweet_id=${mission.tweetId}`
          : mission.twitterUrl || "https://twitter.com/CVMechNFT";
        break;
      case "follow":
        url = `https://twitter.com/intent/follow?screen_name=CVMechNFT`;
        break;
      default:
        url = mission.twitterUrl || "https://twitter.com/CVMechNFT";
    }

    // Open Twitter link in new tab
    window.open(url, "_blank", "noopener,noreferrer");

    // Set completing state
    setCompletingMission(mission.id);

    // Wait 3 seconds then call API
    setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/mission/complete`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId: address,
            missionId: mission.id,
          }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          // Refresh missions to get updated status
          await fetchMissions();
        } else {
          console.error("Failed to complete mission:", data.error);
        }
      } catch (error) {
        console.error("Error completing mission:", error);
      } finally {
        setCompletingMission(null);
      }
    }, 3000);
  };

  return (
    <div className="wrapper">
      <Preloader />
      <Menu />

      <section className="mission">
        <div className="container">
          <div className="row">
            <div className="block-text center">
              <h6 className="sub-heading">
                <span>Missions</span>
              </h6>
              <h3 className="heading" data-aos="fade-up">
                Partner Missions{" "}
                <span style={{ fontSize: "0.6em", fontWeight: "normal" }}>
                  (Coming Soon)
                </span>
              </h3>
              <p className="mb-30">
                This section is dedicated exclusively to CVMech NFT holders.
              </p>
              <p className="mb-30">
                Complete missions, stay active, and earn your share from
                exclusive partner reward pools.
              </p>
            </div>

            {!isConnected && (
              <div className="wallet-required">
                <div className="wallet-required-content">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 8V12M12 16H12.01"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <h4>Wallet Connection Required</h4>
                  <p>
                    Please connect your wallet to start completing missions and
                    earning rewards.
                  </p>
                  <div className="wallet-connect-btn">
                    <ConnectWallet />
                  </div>
                </div>
              </div>
            )}

            {isConnected && (
              <>
                {loading ? (
                  <div className="loading-missions">
                    <p>Loading missions...</p>
                  </div>
                ) : (
                  <>
                    <div className="mission-stats">
                      <div className="stat-item">
                        <div className="stat-value">{stats.completed}</div>
                        <div className="stat-label">Completed</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">{stats.pending}</div>
                        <div className="stat-label">Pending</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">{stats.total}</div>
                        <div className="stat-label">Total Missions</div>
                      </div>
                    </div>

                    <div className="mission__main mission-grid">
                      {missions.length === 0 ? (
                        <div className="no-missions">
                          <p>No missions available at the moment.</p>
                        </div>
                      ) : (
                        missions.map((mission, index) => (
                          <div
                            key={mission.id}
                            className={`mission-box ${
                              mission.isCompleted ? "completed" : "pending"
                            }`}
                            data-aos="fade-up"
                            data-aos-delay={index * 100}
                          >
                            <div className="mission-status">
                              {mission.isCompleted ? (
                                <span className="status-badge completed">
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M13.3333 4L6 11.3333L2.66667 8"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                  Completed
                                </span>
                              ) : (
                                <span className="status-badge pending">
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <circle
                                      cx="8"
                                      cy="8"
                                      r="6"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    />
                                  </svg>
                                  Pending
                                </span>
                              )}
                            </div>
                            <div className="time">
                              {mission.completedAt
                                ? new Date(
                                    mission.completedAt
                                  ).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  })
                                : "Not Started"}
                            </div>
                            <div className="content">
                              <h5 className="title">{mission.title}</h5>
                              <p className="text">{mission.description}</p>
                              <div className="reward">
                                <span className="reward-label">Reward:</span>
                                <span className="reward-value">
                                  {mission.reward}
                                </span>
                              </div>
                              {!mission.isCompleted && (
                                <button
                                  className="mission-action-btn"
                                  onClick={() => handleMissionAction(mission)}
                                  disabled={completingMission === mission.id}
                                >
                                  {completingMission === mission.id ? (
                                    <>
                                      <span className="spinner"></span>
                                      Processing...
                                    </>
                                  ) : (
                                    <>
                                      <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M23 3.00005C22.0424 3.67552 20.9821 4.19216 19.86 4.53005C19.2577 3.83756 18.4573 3.34674 17.567 3.12397C16.6767 2.90121 15.7395 2.95724 14.8821 3.28451C14.0247 3.61179 13.2884 4.19445 12.773 4.95376C12.2575 5.71308 11.9877 6.61238 12 7.53005V8.53005C10.2426 8.57561 8.50127 8.18586 6.93101 7.39549C5.36074 6.60513 4.01032 5.43868 3 4.00005C3 4.00005 -1 13 8 17C5.94053 18.398 3.48716 19.099 1 19C10 24 21 19 21 7.00005C20.9991 6.64931 20.9723 6.29897 20.92 5.95005C21.9406 4.92598 22.6608 3.61457 23 3.00005Z"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                      {mission.action === "like" &&
                                        "Like on Twitter"}
                                      {mission.action === "retweet" &&
                                        "Retweet"}
                                      {mission.action === "follow" &&
                                        "Follow on Twitter"}
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <Footer />
      <ScrollTop />
    </div>
  );
};

export default Mission;
